'use client';

import React from 'react';
import { Squares2X2Icon, Bars3Icon } from '@heroicons/react/24/outline';
import { classnames } from '@/utils/classnames/classnames';
import { ViewMode, ViewToggleProps } from './types';

// EWM-185 grid / list view toggle.
//
// Two-button radio-group sitting in the toolbar's right-side cluster
// next to ResultsCount + SortBy. The selected option gets the primary
// background; the other is plain. Pure presentational — caller wires
// `onChange` to URL refinement so the choice persists across reloads
// and back-button.
const Option = ({
  mode,
  active,
  Icon,
  label,
  onClick,
}: {
  mode: ViewMode;
  active: boolean;
  Icon: typeof Squares2X2Icon;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    aria-label={label}
    onClick={onClick}
    data-mode={mode}
    className={classnames(
      'flex size-9 items-center justify-center rounded-md transition-colors',
      'focus:ring-primary/40 focus:ring-2 focus:outline-hidden',
      active ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100',
    )}
  >
    <Icon className="size-5" aria-hidden />
  </button>
);

const ViewToggle = ({ value, onChange, className = '' }: ViewToggleProps) => {
  return (
    <div
      role="radiogroup"
      aria-label="View mode"
      className={classnames(
        'inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white p-0.5',
        className,
      )}
    >
      <Option
        mode="grid"
        active={value === 'grid'}
        Icon={Squares2X2Icon}
        label="Grid view"
        onClick={() => onChange?.('grid')}
      />
      <Option
        mode="list"
        active={value === 'list'}
        Icon={Bars3Icon}
        label="List view"
        onClick={() => onChange?.('list')}
      />
    </div>
  );
};

export default ViewToggle;
